import { ref, onMounted } from "vue";
import { useAuth } from "./useAuth";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: (callback: (notification: any) => void) => void;
          renderButton: (btn: HTMLElement, config: any) => void;
          disableAutoSelect: () => void;
        };
        oauth2: {
          initTokenClient: (config: any) => {
            requestAccessToken: (options?: any) => void;
          };
        };
      };
    };
  }
}

export const useGoogleAccount = () => {
  const { signInGoogle } = useAuth();

  const currentGoogleEmail = ref<string | null>(null);
  const isGoogleSignedIn = ref(false);

  const loadGoogleIdentityServices = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error("Failed to load Google Identity Services"));
      document.head.appendChild(script);
    });
  };

  const getGoogleAccountEmail = async (): Promise<string | null> => {
    try {
      await loadGoogleIdentityServices();

      if (!window.google?.accounts) {
        return null;
      }

      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string;
      if (!clientId) {
        return null;
      }

      return new Promise(resolve => {
        try {
          window?.google?.accounts?.id?.initialize({
            client_id: clientId,
            callback: async (response: any) => {
              await signInGoogle(response.credential);
            }
          });
          const btn = document.getElementById("google-login-button");
          if (btn) {
            window?.google?.accounts?.id?.renderButton(btn, {
              type: "standard",
              theme: "outline",
              text: "signin_with",
              size: "large",
              width: "320"
            });
          }
          // window?.google?.accounts?.id?.prompt((notification: any) => {
          //   console.log(notification);
          // });
        } catch (error) {
          console.error("Error getting Google account:", error);
          resolve(null);
        }
      });
    } catch (error) {
      console.error("Error loading Google Identity Services:", error);
      return null;
    }
  };

  onMounted(() => {
    if (typeof window !== "undefined") {
      getGoogleAccountEmail();
    }
  });

  return {
    currentGoogleEmail,
    isGoogleSignedIn,
    getGoogleAccountEmail,
    refreshGoogleAccount: getGoogleAccountEmail
  };
};
